import { useState, useCallback, type ReactNode } from 'react';
import { FileText, UserPlus } from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { QuotesListPage } from './QuotesListPage';
import { LeadsListPage } from './leads/LeadsListPage';

type SalesTab = 'leads' | 'quotes';
const TAB_KEY = 'iomi_sales_tab';

const TAB_CONFIG: { id: SalesTab; label: string; icon: typeof FileText }[] = [
  { id: 'leads', label: 'Leads', icon: UserPlus },
  { id: 'quotes', label: 'Quotes', icon: FileText },
];

export function SalesPage() {
  const { activeOrg, loading } = useUserProfile();
  const leadsEnabled = activeOrg?.featureLeadPipeline ?? false;

  const defaultTab: SalesTab = 'quotes';

  const [activeTab, setActiveTab] = useState<SalesTab>(() => {
    if (!leadsEnabled) return 'quotes';
    const stored = localStorage.getItem(TAB_KEY);
    if (stored === 'leads' && leadsEnabled) return 'leads';
    if (stored === 'quotes') return 'quotes';
    return defaultTab;
  });

  const [sidebarContent, setSidebarContent] = useState<ReactNode>(null);
  const handleSidebarContent = useCallback((content: ReactNode) => {
    setSidebarContent(content);
  }, []);

  function switchTab(tab: SalesTab) {
    setActiveTab(tab);
    setSidebarContent(null);
    localStorage.setItem(TAB_KEY, tab);
  }

  if (loading) return null;

  const showTabs = leadsEnabled;
  const visibleTabs = TAB_CONFIG.filter(t => t.id === 'quotes' || (t.id === 'leads' && leadsEnabled));

  const tabContent = (
    <>
      {activeTab === 'leads' && leadsEnabled && (
        <LeadsListPage embedded onSidebarContent={handleSidebarContent} />
      )}
      {(activeTab === 'quotes' || !leadsEnabled) && (
        <QuotesListPage embedded onSidebarContent={handleSidebarContent} />
      )}
    </>
  );

  return (
    <>
      {/* ── Mobile layout ──────────────────────────────────────────── */}
      <div className="md:hidden px-4 pt-4 pb-24">
        <div className="flex items-center gap-4 mb-4">
          <h1 className="font-display text-h1 text-midnight shrink-0">Sales</h1>

          {showTabs && (
            <div className="flex items-center gap-1 p-1 bg-mist rounded-lg">
              {visibleTabs.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => switchTab(t.id)}
                  className={`flex items-center justify-center gap-1.5 h-8 px-4 rounded-md text-[13px] font-semibold transition-colors duration-150 ${
                    activeTab === t.id ? 'bg-white text-midnight shadow-sm' : 'text-slate hover:text-midnight'
                  }`}
                >
                  <t.icon size={14} />
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {tabContent}
      </div>

      {/* ── Desktop layout (md+) ───────────────────────────────────── */}
      <div className="hidden md:block px-6 pt-4 pb-24 max-w-6xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-h1 text-midnight">Sales</h1>
        </div>

        <div className="flex gap-6 items-start">
          {/* Left sidebar: vertical tabs + stats */}
          {showTabs && (
            <aside className="w-52 shrink-0 sticky top-20">
              <nav className="flex flex-col gap-1">
                {visibleTabs.map(t => {
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => switchTab(t.id)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-left transition-all duration-150 ${
                        isActive
                          ? 'bg-plum text-white shadow-brand-sm'
                          : 'text-slate hover:bg-mist hover:text-midnight'
                      }`}
                    >
                      <t.icon size={16} />
                      {t.label}
                    </button>
                  );
                })}
              </nav>

              {sidebarContent && (
                <>
                  <div className="my-4 border-t border-stone" />
                  {sidebarContent}
                </>
              )}
            </aside>
          )}

          {/* Right content area */}
          <div className="flex-1 min-w-0">
            {tabContent}
          </div>
        </div>
      </div>
    </>
  );
}
